import React, { useMemo } from 'react';

import { notEmpty } from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';

import { CATEGORY_NAME_INDEXES } from '../../../../core/constants';
import { ConsolidatedForecastReportStructure, ConsolidatedForecastReportTab, VIEW_TYPES } from '../../../../core/types';
import { FRForecastLayoutItem, FRForecastLayoutItemDisplayType, ForecastTree } from '../types';
import { ForecastUpdating } from '../../components/forecast-updating';
import { PredictionsTotalsPageWrapper } from '../predictions-totals-page-wrapper';
import { useFRAccountNames } from '../hooks/fr/useFRAccountNames/useFRAccountNames';
import { useFRForecastGrid } from '../hooks/fr/useFRForecastGrid/useFRForecastGrid';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';

interface PredictionsSubTabFRProps {
  tab: ConsolidatedForecastReportTab;
}

const getLayout = (trees: Record<string, ForecastTree>, tab: ConsolidatedForecastReportTab): FRForecastLayoutItem[] => {
  return tab.children
    .map(child => {
      const datasource = child.find_in as keyof ConsolidatedForecastReportStructure;
      const index = trees[datasource].index;
      const node = index[child.ref];

      if (node) {
        return {
          category: CATEGORY_NAME_INDEXES.income,
          datasource,
          displayType:
            node.type === 'top-level-static-name-only'
              ? FRForecastLayoutItemDisplayType.headingWithTopLevelAccounts
              : FRForecastLayoutItemDisplayType.topLevelAccounts,
          nodes: [node]
        };
      }
    })
    .filter(notEmpty);
};

export const PredictionsSubTabFR = ({ tab }: PredictionsSubTabFRProps) => {
  const accountNameMap = useFRAccountNames();
  const {
    forecast,
    forecastReportV2: forecastReport,
    isLoading,
    totalCashPredictRow,
    viewType
  } = usePredictionsInfoContext();
  const getLayoutFunc = useMemo(() => (trees: Record<string, ForecastTree>) => getLayout(trees, tab), [tab]);

  const { columns, columnsWithFYInfo, dataGridRows } = useFRForecastGrid(
    forecast,
    forecastReport,
    accountNameMap,
    getLayoutFunc
  );

  if (!forecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <PredictionsTotalsPageWrapper
        columns={viewType === VIEW_TYPES.CASH_FLOW ? columns : columnsWithFYInfo}
        totalAccountRows={dataGridRows}
        totalCashPredictRow={totalCashPredictRow}
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
